/**
 * Send a question for review, as the web app's dialog does: pick reviewers
 * from the organisation, add a message, optionally stop reviewers editing
 * the answer. The question becomes Awaiting review and each reviewer is
 * emailed a link that opens it in mytender (which moves it to In review when
 * they arrive).
 */
import { ArrowLeftIcon, SearchIcon } from "@/design/icons";
import { type FC, useEffect, useMemo, useState } from "react";

import { cn } from "@/agents-v2/utils/cn";
import { Checkbox } from "@/design/ui/checkbox";
import { Input } from "@/design/ui/input";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/design/ui/sheet";
import { Textarea } from "@/design/ui/textarea";

import { type OrgUser, type Question, fetchOrganisationUsers } from "../tender/questionsApi";

export type ReviewPayload = { reviewers: string[]; message: string; restrict_editing: boolean };

export type SendForReviewSheetProps = {
  question: Question | null;
  token: string;
  onClose: () => void;
  onSend: (payload: ReviewPayload) => Promise<{ emailed: string[]; skipped: string[] }>;
};

export const SendForReviewSheet: FC<SendForReviewSheetProps> = (props) => (
  <Sheet open={Boolean(props.question)} onOpenChange={(o) => !o && props.onClose()}>
    <SheetContent side="right" className="w-full max-w-none gap-0 p-0 sm:max-w-none [&>button]:hidden">
      {props.question && <Body key={props.question.section_id} {...props} question={props.question} />}
    </SheetContent>
  </Sheet>
);

const displayName = (u: OrgUser): string =>
  [u.firstname, u.lastname]
    .filter((p) => p && p.trim())
    .join(" ")
    .trim() || u.username;

const Body: FC<SendForReviewSheetProps & { question: Question }> = ({ question, token, onClose, onSend }) => {
  const [users, setUsers] = useState<OrgUser[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  // The question's current reviewers start selected ("Unassigned" is
  // mytender's word for none), pruned to members once the list arrives.
  const [selected, setSelected] = useState<Set<string>>(
    () =>
      new Set(
        (question.reviewer || "")
          .split(",")
          .map((r) => r.trim())
          .filter((r) => r && r !== "Unassigned")
      )
  );
  const [query, setQuery] = useState("");
  const [message, setMessage] = useState("");
  const [restrict, setRestrict] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchOrganisationUsers(token)
      .then((list) => {
        if (cancelled) return;
        setUsers(list.sort((a, b) => displayName(a).localeCompare(displayName(b))));
        const members = new Set(list.map((u) => u.username));
        setSelected((prev) => new Set([...prev].filter((r) => members.has(r))));
      })
      .catch(() => {
        if (!cancelled) setLoadError("Couldn't load your organisation's users.");
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (users ?? []).filter((u) => !q || `${displayName(u)} ${u.email} ${u.username}`.toLowerCase().includes(q));
  }, [users, query]);
  const count = selected.size;

  const toggle = (username: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(username)) next.delete(username);
      else next.add(username);
      return next;
    });

  const send = async () => {
    if (!count) return;
    setSending(true);
    setError(null);
    try {
      await onSend({ reviewers: [...selected], message: message.trim(), restrict_editing: restrict });
    } catch (e) {
      const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Couldn't send for review. Try again.");
      setSending(false);
    }
  };

  return (
    <div className="flex h-full flex-col bg-surface">
      <div className="flex h-11 shrink-0 items-center gap-1 border-b border-line px-2">
        <button
          type="button"
          aria-label="Back"
          onClick={onClose}
          className="flex size-7 items-center justify-center rounded-sm text-fg-2 hover:bg-subtle hover:text-fg"
        >
          <ArrowLeftIcon className="size-4" />
        </button>
        <span className="text-sm font-medium text-fg-3">Send for review</span>
      </div>
      <div className="px-4 pb-2 pt-4">
        <SheetTitle className="text-lg font-semibold leading-snug text-fg">
          {[question.section_number, question.heading || "Untitled question"].filter(Boolean).join(" ")}
        </SheetTitle>
        <SheetDescription className="mt-0.5 text-sm text-fg-3">
          Reviewers are emailed a link to this question in mytender. It moves to Awaiting review now, and to In review
          when a reviewer opens it.
        </SheetDescription>
      </div>

      <div className="px-3 pb-1">
        <div className="relative">
          <SearchIcon className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-fg-3" />
          <Input
            id="reviewer-search"
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search your organisation"
            className="h-[34px] bg-subtle pl-8"
          />
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {loadError ? (
          <p className="px-4 py-3 text-sm text-danger">{loadError}</p>
        ) : users === null ? (
          <p className="px-4 py-3 text-sm text-fg-3">Loading…</p>
        ) : !shown.length ? (
          <p className="px-4 py-3 text-sm text-fg-3">
            {query ? "No one matches." : "No one else is in your organisation yet."}
          </p>
        ) : (
          shown.map((u, i) => {
            const on = selected.has(u.username);
            return (
              <label
                key={u.username}
                className={cn(
                  "flex cursor-pointer items-center gap-2.5 px-4 py-2 hover:bg-subtle",
                  i && "border-t border-line/70"
                )}
              >
                <Checkbox checked={on} onCheckedChange={() => toggle(u.username)} aria-label={displayName(u)} />
                <span className="min-w-0 flex-1">
                  <span className={cn("block truncate", on ? "font-medium text-fg" : "text-fg-2")}>
                    {displayName(u)}
                  </span>
                  <span className="block truncate text-xs text-fg-3">{u.email}</span>
                </span>
              </label>
            );
          })
        )}
      </div>

      <div className="shrink-0 border-t border-line px-4 pb-3 pt-3">
        <Textarea
          id={`review-message-${question.section_id}`}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Message to the reviewers (optional)"
          className="min-h-[64px] resize-y border-line bg-surface text-sm leading-relaxed focus-visible:border-accent focus-visible:ring-0"
        />
        <label className="mt-2.5 flex cursor-pointer items-center gap-2 text-sm text-fg-2">
          <Checkbox checked={restrict} onCheckedChange={(v) => setRestrict(v === true)} />
          Reviewers can comment but not edit the answer
        </label>
        {error && <p className="mt-2 text-xs text-danger">{error}</p>}
        <button
          type="button"
          disabled={sending || !count}
          onClick={() => void send()}
          className="mt-3 inline-flex w-full items-center justify-center rounded-md bg-accent px-3 py-1.5 text-base font-semibold text-fg-inverse transition-colors duration-fast hover:bg-accent-hover disabled:opacity-40"
        >
          {sending ? "Sending…" : count ? `Send to ${count} reviewer${count === 1 ? "" : "s"}` : "Choose a reviewer"}
        </button>
      </div>
    </div>
  );
};
